import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { InfiniteData } from '@tanstack/react-query';
import { TFunction } from 'i18next';

import { useIsPotentiallyAuthenticated } from 'services/auth/auth';
import { createHandle } from 'services/breadcrumbs/breadcrumbs';
import { ConnectionsResponseDTO, useInfiniteConnections } from 'services/repositories/connections/connections';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { Connectors, DocumentType, NetworkRoutes, PriceCatalogRoutes, RelationshipStatus } from 'support/types';
import { EmptyState } from 'components/EmptyState/EmptyState';
import { Filters } from 'components/Filters/Filters';
import { Page } from 'components/Page/Page';

import { InfiniteScroll } from '../../components/ListTable/InfiniteScroll/InfiniteScroll';
import { CatalogPartnerDetails } from './CatalogPartnerDetails/CatalogPartnerDetails';
import { CatalogPartnerList } from './CatalogPartnerList/CatalogPartnerList';
import { PriceCatalogsFilterKeys, usePriceCatalogsFilters } from './filters';
import { CatalogPartnerListItem } from './types';

export function PriceCatalogsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { relationshipId } = useParams();
  const [catalogPartnerDetailsSlideOpen, setCatalogPartnerDetailsSlideOpen] = useState(Boolean(relationshipId));

  useEffect(() => {
    if (relationshipId) {
      setCatalogPartnerDetailsSlideOpen(true);
    }
  }, [relationshipId]);

  const isAuthenticated = useIsPotentiallyAuthenticated();
  const { data: currentPartner, isLoading: isLoadingCurrentPartner } = useCurrentPartner();
  const { filterValues, updateFilters, clearAllFilters, filters } = usePriceCatalogsFilters();
  const {
    data: connectionsData,
    isLoading: isLoadingConnections,
    isFetchingNextPage,
    fetchNextPage,
    hasPreviousPage,
    hasNextPage,
  } = useInfiniteConnections({
    variables: {
      query: {
        filterValues,
        perPage: '100',
        include: 'senderRelationships.notificationLogs,receiverRelationships.notificationLogs',
      },
    },
    // Only fetch once auth is confirmed. Without this gate, an expired session
    // triggers a 401 on every render cycle: the interceptor calls
    // setPotentiallyAuthenticated(false) which dispatches a storage event,
    // causing useSessionStorage in useNewFilters to return a new filterValues
    // reference, which changes the query key, which triggers another fetch,
    // which gets another 401 — cascading into "Maximum update depth exceeded"
    // (PORTAL-2PG / PORTAL-2PH / PORTAL-2PJ / PORTAL-2PK).
    enabled: Boolean(isAuthenticated),
  });

  const hasFilterApplied = filterValues.some(
    (filter) =>
      filter.key === PriceCatalogsFilterKeys.partnerName || filter.key === PriceCatalogsFilterKeys.lastRequestStatus,
  );

  const catalogPartners = useMemo(() => {
    if (!currentPartner || !connectionsData) {
      return [];
    }

    return buildCatalogPartnerList(currentPartner.id, connectionsData);
  }, [currentPartner, connectionsData]);

  const isLoading = isLoadingCurrentPartner || isLoadingConnections;

  return (
    <Page>
      <Page.Head title={t('common:mainMenu.priceCatalogs')} description={t('priceCatalogs:partnerList.subtitle')}>
        <Filters
          propertyFilters={filters}
          onFiltersChange={updateFilters}
          filterValues={filterValues}
          clearAllFilters={clearAllFilters}
        />
      </Page.Head>

      <Page.Section>
        {isLoading || !!catalogPartners.length || hasFilterApplied ? (
          <CatalogPartnerList
            data={catalogPartners}
            isLoading={isLoading}
            onClearFilters={clearAllFilters}
            pagination={
              <InfiniteScroll
                hasMore={Boolean(hasNextPage)}
                loadMore={fetchNextPage}
                loading={isFetchingNextPage}
                noMoreItemsEnabled={Boolean(hasPreviousPage)}
              />
            }
          />
        ) : (
          <EmptyState
            title={t('priceCatalogs:partnerList.emptyState.title')}
            description={t('priceCatalogs:partnerList.emptyState.description')}
            ctaText={t('priceCatalogs:partnerList.emptyState.cta')}
            ctaOnClick={() => navigate(NetworkRoutes.root)}
            icon={<BookOpenIcon />}
          />
        )}
      </Page.Section>
      <CatalogPartnerDetails
        open={catalogPartnerDetailsSlideOpen}
        partnerIsSender={
          catalogPartners.find((partner) => partner.catalogRelationshipId === relationshipId)?.partnerIsSender ?? false
        }
        senderUsesWebEDI={
          catalogPartners.find((partner) => partner.catalogRelationshipId === relationshipId)?.senderUsesWebEDI ?? false
        }
        onClose={() => {
          setCatalogPartnerDetailsSlideOpen(false);
          navigate(PriceCatalogRoutes.root);
        }}
        relationshipId={relationshipId}
      />
    </Page>
  );
}

const buildCatalogPartnerList = (
  currentPartnerId: string,
  connectionsData: InfiniteData<ConnectionsResponseDTO>,
): Array<CatalogPartnerListItem> => {
  return connectionsData.pages
    .flatMap((page) => page.data)
    .reduce((acc, partner) => {
      const catalogRelationship = [
        ...(partner.sender_relationships ?? []),
        ...(partner.receiver_relationships ?? []),
      ].find(
        (relationship) =>
          relationship.message_type === DocumentType.productCatalog &&
          (relationship.sender_partner.id === currentPartnerId ||
            relationship.receiver_partner?.id === currentPartnerId) &&
          [RelationshipStatus.ACTIVE, RelationshipStatus.LIVE].includes(RelationshipStatus[relationship.status]),
      );

      if (!catalogRelationship) {
        return acc;
      }

      return [
        ...acc,
        {
          partner,
          lastRequestStatus:
            'last_request_status' in catalogRelationship ? catalogRelationship.last_request_status : undefined,
          partnerIsSender: catalogRelationship.sender_partner.id === currentPartnerId,
          senderUsesWebEDI: catalogRelationship.sender_integration?.production?.connector === Connectors.WEB_EDI,
          catalogRelationshipId: catalogRelationship.id,
          messageCount: catalogRelationship.messages_count,
          lastMessageCreatedAt: catalogRelationship.latest_live_message?.created_at,
        },
      ];
    }, [] as Array<CatalogPartnerListItem>);
};

PriceCatalogsPage.handle = createHandle(() => ({
  label: (t: TFunction) => {
    return t('priceCatalogs:breadcrumb');
  },
  to: routeToPage(PriceCatalogRoutes.root),
  key: PriceCatalogRoutes.root,
}));
